/**
 * Exercice AI Export/Import Utilities
 *
 * Provides functions to export exercises for AI correction and import corrected data.
 * Used by admins with canUseAICorrection permission.
 */

import { Exercice, Question } from '../types';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Fields exportable for AI correction (content fields)
 */
export const EXPORTABLE_FIELDS = [
  'titre',
  'description',
  'niveau',
  'questions',
  'competencesVisees',
  'tempsEstimeMinutes'
] as const;

// ============================================================================
// TYPES
// ============================================================================

/**
 * Structure exported for AI correction
 */
export interface ExerciceAIExportData {
  // Editable content fields
  titre: string;
  description: string;
  niveau: 'facile' | 'moyen' | 'difficile';
  questions: QuestionAIExportData[];
  competencesVisees: string[];
  tempsEstimeMinutes?: number;

  // Context fields (read-only, for AI reference)
  matiereName: string;
  leconName: string;
  chapitreName: string;
}

/**
 * Question structure for AI export (without internal IDs)
 */
export interface QuestionAIExportData {
  texte: string;
  type: 'choix_multiple' | 'vrai_faux' | 'texte_libre';
  reponseCorrecte: string;
  explication: string;
  options?: string[];
  points: number;
  latexillustration?: string;
  illustrationLatex?: string;
  lienLatex?: string;
}

/**
 * Context information for export
 */
export interface ExerciceContext {
  matiereName: string;
  leconName: string;
  chapitreName: string;
}

/**
 * Import validation result
 */
export interface AIImportValidationResult {
  success: boolean;
  data?: Partial<Omit<Exercice, 'id' | 'chapitreId' | 'leconId'>>;
  errors?: AIImportFieldError[];
}

/**
 * Field-specific import error
 */
export interface AIImportFieldError {
  field: string;
  message: string;
  received?: unknown;
  expected?: string;
}

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

/**
 * Generate the AI prompt with exercise JSON
 *
 * @param exercice - The exercise to export
 * @param context - Context information (matiere, lecon, chapitre names)
 * @returns Full prompt text with instructions and JSON
 */
export function generateAIPrompt(
  exercice: Exercice,
  context: ExerciceContext
): string {
  // Build export data (only content fields)
  const exportData: ExerciceAIExportData = {
    titre: exercice.titre,
    description: exercice.description,
    niveau: exercice.niveau,
    questions: exercice.questions.map(q => ({
      texte: q.texte,
      type: q.type,
      reponseCorrecte: q.reponseCorrecte,
      explication: q.explication,
      options: q.options,
      points: q.points,
      latexillustration: q.latexillustration,
      illustrationLatex: q.illustrationLatex,
      lienLatex: q.lienLatex
    })),
    competencesVisees: exercice.competencesVisees || [],
    tempsEstimeMinutes: exercice.tempsEstimeMinutes,
    // Context fields
    matiereName: context.matiereName,
    leconName: context.leconName,
    chapitreName: context.chapitreName
  };

  // Build the prompt
  const prompt = `Tu es un assistant specialise dans la correction d'exercices pedagogiques.

CONTEXTE:
- Matiere: ${context.matiereName}
- Chapitre: ${context.chapitreName}
- Lecon: ${context.leconName}

INSTRUCTIONS:
1. Corrige les fautes d'orthographe et de grammaire
2. Ameliore la clarte des formulations si necessaire
3. Les questions sont au format QCM (Questions a Choix Multiples)
4. Assure-toi qu'il y a entre 3 et 5 questions par exercice
5. Si moins de 3 questions, ajoute des questions pertinentes
6. Conserve fidelement le formatage LaTeX (entre $ ou $$)
7. Ne modifie PAS les champs de contexte (matiereName, leconName, chapitreName)
8. Verifie la coherence entre la description et les questions (la description doit correspondre au contenu des questions)
9. Verifie la coherence entre les competences visees et les questions (les competences listees doivent etre reellement testees par les questions)
10. ILLUSTRATIONS LATEX: Pour les matieres scientifiques (mathematiques, physique, chimie, SVT), genere une illustration LaTeX complete dans le champ "illustrationLatex" de chaque question si l'exercice le necessite (geometrie, graphiques, schemas, circuits, etc.). Utilise TikZ ou des environnements LaTeX standards. Pour les matieres litteraires (francais, philosophie, histoire, etc.), ce champ n'est generalement pas necessaire.

EXERCICE A CORRIGER:

\`\`\`json
${JSON.stringify(exportData, null, 2)}
\`\`\`

FORMAT DE RETOUR ATTENDU:
Retourne uniquement le JSON corrige, SANS les champs de contexte (matiereName, leconName, chapitreName):

\`\`\`json
{
  "titre": "...",
  "description": "...",
  "niveau": "facile|moyen|difficile",
  "questions": [
    {
      "texte": "...",
      "type": "choix_multiple|vrai_faux|texte_libre",
      "reponseCorrecte": "...",
      "explication": "...",
      "options": ["A", "B", "C", "D"],
      "points": 1,
      "illustrationLatex": "\\\\begin{tikzpicture}...\\\\end{tikzpicture}" // optionnel, pour matieres scientifiques
    }
  ],
  "competencesVisees": [...],
  "tempsEstimeMinutes": number
}
\`\`\``;

  return prompt;
}

// ============================================================================
// IMPORT FUNCTIONS
// ============================================================================

/**
 * Extract JSON from AI response text
 * Handles cases where AI wraps JSON in markdown code blocks
 *
 * @param text - Raw text from AI response
 * @returns Extracted JSON string or null if not found
 */
export function extractJsonFromAIResponse(text: string): string | null {
  // Try to find JSON in code block
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }

  // Try to find raw JSON object
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    return jsonMatch[0].trim();
  }

  return null;
}

/**
 * Validate a single field from imported JSON
 */
function validateField(
  field: string,
  value: unknown,
  errors: AIImportFieldError[]
): void {
  switch (field) {
    case 'titre':
      if (typeof value !== 'string') {
        errors.push({ field, message: 'Le titre doit etre une chaine de caracteres', received: typeof value, expected: 'string' });
      } else if (value.trim().length === 0) {
        errors.push({ field, message: 'Le titre ne peut pas etre vide', received: value, expected: 'non-empty string' });
      } else if (value.length > 200) {
        errors.push({ field, message: 'Le titre ne peut pas depasser 200 caracteres', received: value.length, expected: 'max 200 characters' });
      }
      break;

    case 'description':
      if (typeof value !== 'string') {
        errors.push({ field, message: 'La description doit etre une chaine de caracteres', received: typeof value, expected: 'string' });
      } else if (value.length > 2000) {
        errors.push({ field, message: 'La description ne peut pas depasser 2000 caracteres', received: value.length, expected: 'max 2000 characters' });
      }
      break;

    case 'niveau':
      if (!['facile', 'moyen', 'difficile'].includes(value as string)) {
        errors.push({ field, message: 'Le niveau doit etre "facile", "moyen" ou "difficile"', received: value, expected: '"facile" | "moyen" | "difficile"' });
      }
      break;

    case 'questions':
      if (!Array.isArray(value)) {
        errors.push({ field, message: 'Les questions doivent etre un tableau', received: typeof value, expected: 'array' });
      } else if (value.length < 1) {
        errors.push({ field, message: 'Il doit y avoir au moins 1 question', received: value.length, expected: 'min 1 question' });
      } else {
        // Validate each question
        value.forEach((q, index) => {
          validateQuestion(q, index, errors);
        });
      }
      break;

    case 'competencesVisees':
      if (!Array.isArray(value)) {
        errors.push({ field, message: 'Les competences visees doivent etre un tableau', received: typeof value, expected: 'array' });
      } else {
        value.forEach((comp, index) => {
          if (typeof comp !== 'string') {
            errors.push({ field: `${field}[${index}]`, message: 'Chaque competence doit etre une chaine de caracteres', received: typeof comp, expected: 'string' });
          }
        });
      }
      break;

    case 'tempsEstimeMinutes':
      if (value !== undefined && value !== null) {
        if (typeof value !== 'number' || !Number.isInteger(value)) {
          errors.push({ field, message: 'Le temps estime doit etre un entier', received: typeof value, expected: 'integer' });
        } else if (value < 0 || value > 180) {
          errors.push({ field, message: 'Le temps estime doit etre entre 0 et 180 minutes', received: value, expected: '0-180' });
        }
      }
      break;
  }
}

/**
 * Validate a single question
 */
function validateQuestion(
  question: unknown,
  index: number,
  errors: AIImportFieldError[]
): void {
  const prefix = `questions[${index}]`;

  if (typeof question !== 'object' || question === null) {
    errors.push({ field: prefix, message: 'Chaque question doit etre un objet', received: typeof question, expected: 'object' });
    return;
  }

  const q = question as Record<string, unknown>;

  // Required fields
  if (typeof q.texte !== 'string' || q.texte.trim().length === 0) {
    errors.push({ field: `${prefix}.texte`, message: 'Le texte de la question est requis', received: q.texte, expected: 'non-empty string' });
  }

  if (!['choix_multiple', 'vrai_faux', 'texte_libre'].includes(q.type as string)) {
    errors.push({ field: `${prefix}.type`, message: 'Le type doit etre "choix_multiple", "vrai_faux" ou "texte_libre"', received: q.type, expected: '"choix_multiple" | "vrai_faux" | "texte_libre"' });
  }

  if (typeof q.reponseCorrecte !== 'string') {
    errors.push({ field: `${prefix}.reponseCorrecte`, message: 'La reponse correcte est requise', received: q.reponseCorrecte, expected: 'string' });
  }

  if (typeof q.explication !== 'string') {
    errors.push({ field: `${prefix}.explication`, message: 'L\'explication est requise', received: q.explication, expected: 'string' });
  }

  // Options validation for QCM
  if (q.type === 'choix_multiple') {
    if (!Array.isArray(q.options)) {
      errors.push({ field: `${prefix}.options`, message: 'Les options sont requises pour les QCM', received: q.options, expected: 'array' });
    } else if (q.options.length < 2 || q.options.length > 6) {
      errors.push({ field: `${prefix}.options`, message: 'Il doit y avoir entre 2 et 6 options', received: q.options.length, expected: '2-6 options' });
    }
    // Note: reponseCorrecte peut etre une lettre (A, B, C...) ou le texte complet
  }

  // Points validation
  if (typeof q.points !== 'number' || q.points < 0) {
    errors.push({ field: `${prefix}.points`, message: 'Les points doivent etre un nombre positif', received: q.points, expected: 'positive number' });
  }
}

/**
 * Validate and parse JSON imported from AI
 *
 * @param jsonText - Raw JSON text to validate
 * @returns Validation result with parsed data or errors
 */
export function validateAIImport(jsonText: string): AIImportValidationResult {
  const errors: AIImportFieldError[] = [];

  // Try to extract JSON from response
  const extractedJson = extractJsonFromAIResponse(jsonText);
  if (!extractedJson) {
    return {
      success: false,
      errors: [{ field: 'json', message: 'Impossible de trouver du JSON valide dans la reponse' }]
    };
  }

  // Try to parse JSON
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(extractedJson);
  } catch (e) {
    return {
      success: false,
      errors: [{ field: 'json', message: `JSON invalide: ${(e as Error).message}` }]
    };
  }

  // Validate each field
  EXPORTABLE_FIELDS.forEach(field => {
    if (field in parsed) {
      validateField(field, parsed[field], errors);
    }
  });

  if (errors.length > 0) {
    return { success: false, errors };
  }

  // Build partial exercice data (only include fields that were validated)
  const data: Partial<Omit<Exercice, 'id' | 'chapitreId' | 'leconId'>> = {};

  if (parsed.titre !== undefined) data.titre = parsed.titre as string;
  if (parsed.description !== undefined) data.description = parsed.description as string;
  if (parsed.niveau !== undefined) data.niveau = parsed.niveau as 'facile' | 'moyen' | 'difficile';
  if (parsed.competencesVisees !== undefined) data.competencesVisees = parsed.competencesVisees as string[];
  if (parsed.tempsEstimeMinutes !== undefined) data.tempsEstimeMinutes = parsed.tempsEstimeMinutes as number;

  // Process questions - preserve IDs from existing questions if possible
  if (parsed.questions !== undefined && Array.isArray(parsed.questions)) {
    data.questions = (parsed.questions as QuestionAIExportData[]).map((q, index) => ({
      id: `q_${index}_${Date.now()}`, // Generate new ID for new questions
      texte: q.texte,
      type: q.type,
      reponseCorrecte: q.reponseCorrecte,
      explication: q.explication,
      options: q.options,
      points: q.points,
      latexillustration: q.latexillustration,
      illustrationLatex: q.illustrationLatex,
      lienLatex: q.lienLatex
    } as Question));
  }

  return { success: true, data };
}

/**
 * Merge imported data with existing exercise data
 * Only updates fields that are present in the import
 *
 * @param existing - Existing exercise data
 * @param imported - Imported data from AI
 * @returns Merged exercise data
 */
export function mergeExerciceData(
  existing: Exercice,
  imported: Partial<Omit<Exercice, 'id' | 'chapitreId' | 'leconId'>>
): Exercice {
  return {
    ...existing,
    ...imported,
    // Preserve IDs and metadata
    id: existing.id,
    chapitreId: existing.chapitreId,
    leconId: existing.leconId,
    dateCreation: existing.dateCreation,
    statut: existing.statut,
    datePublication: existing.datePublication,
    historiqueStatut: existing.historiqueStatut,
    estActif: existing.estActif
  };
}
